import { defineComponent as _defineComponent } from 'vue'
import {
  offset,
  flip,
  size,
  OffsetOptions,
  UseFloatingOptions,
} from '@floating-ui/vue'
import { computed, ref, watch } from 'vue'
import { useDebounce } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { SetRequired } from 'type-fest'

import { useCommandStore } from './command/useCommandStore'
import CommandList from '@/components/core/command/CommandList.vue'
import CommandGroup from '@/components/core/command/CommandGroup.vue'
import CommandItem from '@/components/core/command/CommandItem.vue'
import LCommand from '@/components/core/command/Command.vue'
import { useDismiss, usePopover } from '@/composables/popover'
import { useSelectedTrip } from '@/composables/trip'
import { useContext } from '@/composables/context'
import { Location, LocationAutocompleteResult, Waypoint } from '@/types/trip'
import { Accommodation } from '@/types/accommodation'
import { getFullAddress } from '@/utils/utility-manager'
import { locationToAutocompleteResult } from '@/utils/location'

/**
 * Type for accommodation that ensures location and waypoint are defined
 */
type AccommodationWithLocation = SetRequired<
  Accommodation,
  'location' | 'waypoint'
>


export default /*#__PURE__*/_defineComponent({
  __name: 'CommandLocationSearch',
  props: {
    storeId: null,
    waypoints: { default: () => [] },
    accommodations: { default: () => [] },
    location: { default: undefined },
    width: { default: undefined },
    offset: { default: () => 4 },
    placement: { default: 'bottom-end' },
    placeholder: { default: undefined }
  },
  emits: ["update:location"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:location', value?: LocationAutocompleteResult): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { storeId: string, waypoints: Waypoint[], accommodations: AccommodationWithLocation[], location: LocationAutocompleteResult, width: number, offset: OffsetOptions, placement: UseFloatingOptions['placement'], placeholder: string };





const { $axios } = useContext()
const trip = useSelectedTrip()

const commandStore = useCommandStore(props.storeId)
const { value: selectedCommandItem, search } = storeToRefs(commandStore)
const commandRef = ref<InstanceType<typeof LCommand> | null>(null)

// Popover Functionality -------------------------------------------------------

const isOpen = ref(false)

const { referenceRef, floatingRef, floatingStyles } = usePopover({
  placement: props.placement,
  isOpen,
  middleware: [
    offset(props.offset),
    flip({ padding: 16, fallbackPlacements: ['top-end'] }),
    size({
      padding: 16,
      apply({ rects, elements, availableHeight }) {
        Object.assign(elements.floating.style, {
          width: `${props.width ?? rects.reference.width}px`,
          minHeight: '160px',
          maxHeight: `${Math.min(320, availableHeight)}px`,
        })
      },
    }),
  ],
})

useDismiss({ referenceRef, floatingRef, isOpen })

// Location Search Logic -------------------------------------------------------

const isFetching = ref(false)
const query = ref<string | null>(props.location?.title ?? null)
const debouncedQuery = useDebounce(query, 250)
const items = ref<LocationAutocompleteResult[]>([])

/**
 * Determines if the currently selected location is from existing waypoints/accommodations
 */
const isSelectedLocationFromModel = computed(() => {
  return (
    props.waypoints?.some(
      (waypoint) => waypoint.location.placeId === props.location?.id,
    ) ||
    props.accommodations?.some(
      (accommodation) => accommodation.location.placeId === props.location?.id,
    )
  )
})

/**
 * Filtered locations based on search results and existing items
 */
const locations = computed(() => {
  if (items.value.length) {
    // Filter out locations that already exist in waypoints or accommodations
    return items.value.filter((location) => {
      return (
        !props.waypoints?.some(
          (waypoint) => waypoint.location.placeId === location.id,
        ) &&
        !props.accommodations?.some(
          (accommodation) => accommodation.location.placeId === location.id,
        )
      )
    })
  }

  // Show the currently selected location if it's not from existing data
  if (
    props.location &&
    !isSelectedLocationFromModel.value &&
    debouncedQuery.value === props.location?.title
  ) {
    return [props.location]
  }

  return []
})

/**
 * Function to check if the search query meets the requirements for searching
 * @param query The search query to check
 */
function isSufficientQuery(query: string | null): boolean {
  return !!query && query.length > 3 && query !== props.location?.title
}

/**
 * Open the popover when an input change is detected and the popover is not
 * already open
 * This can be the case, when the user tabs into the input field.
 * The extra check is needed to prevent the popover from opening again
 * after selecting a location.
 */
watch(query, (newValue) => {
  const trimmed = (newValue ?? '').trim()
  if (!trimmed) {
    return
  }
  if (!isOpen.value && trimmed !== (props.location?.title ?? '')) {
    isOpen.value = true
  }
})

// Reset query when the popover is closed
watch(isOpen, (newIsOpen) => {
  if (!newIsOpen) {
    query.value = props.location?.title ?? null
    selectedCommandItem.value = ''
  }
})

/**
 * Search for locations when query changes and met the requirements
 */
watch(debouncedQuery, async (newQuery) => {
  if (!isSufficientQuery(newQuery)) {
    items.value = []
    return
  }

  isFetching.value = true
  try {
    const { data } = await $axios.get<{
      data: { predictions: LocationAutocompleteResult[] }
    }>(`/api/trips/${trip.value._id}/waypoints/search?q=${newQuery}`)

    items.value = data.data.predictions
  } finally {
    isFetching.value = false
  }
})

/**
 * Sync location selection and search state
 */
watch(
  [() => props.location, debouncedQuery],
  ([newLocation, newQuery], [oldLocation, oldQuery]) => {
    // Update input when location changes
    if (newLocation && newLocation?.id !== oldLocation?.id) {
      query.value = newLocation?.title ?? null
      selectedCommandItem.value = newLocation?.id ?? ''
    }

    // Update search state based on query changes
    if (newQuery === newLocation?.title) {
      search.value = ''
    } else if (newQuery !== oldQuery) {
      search.value = newQuery ?? ''
    }
  },
  { immediate: true },
)

// Event Handlers --------------------------------------------------------------

function onLocationSelect(location: Location | LocationAutocompleteResult) {
  const locationResult =
    'name' in location ? locationToAutocompleteResult(location) : location

  // Skip if the same location is selected again and reset the query
  if (locationResult.id === props.location?.id) {
    query.value = props.location.title
    isOpen.value = false
    return
  }

  emit('update:location', locationResult)
  isOpen.value = false
}

// Prevent default tab behavior when the popover is open
function onKeyDown(event: KeyboardEvent) {
  if (event.key === 'Tab' && isOpen.value) {
    event.preventDefault()
    event.stopPropagation()
  }
}

function onInput(value: string) {
  query.value = value
}

return { __sfc: true,props, emit, $axios, trip, commandStore, selectedCommandItem, search, commandRef, isOpen, referenceRef, floatingRef, floatingStyles, isFetching, query, debouncedQuery, items, isSelectedLocationFromModel, locations, isSufficientQuery, onLocationSelect, onKeyDown, onInput, CommandList, CommandGroup, CommandItem, LCommand, getFullAddress }
}

})